// trip-feed.js — sync trips from a calendar feed into preferences.trips.
//
// Feeds an existing system rather than adding one: Daily already surfaces the
// next trip, and UseItOrLoseIt already flags food that will spoil while you are
// away. This just stops those depending on remembering to type trips in.
//
// ── The merge rule ─────────────────────────────────────────────────────────
// Imported trips carry `source: "feed"` and the feed's own id. A sync only ever
// touches rows it previously created. Anything you typed by hand is untouched,
// always — losing a manually entered trip to a background sync would be a much
// worse failure than showing a duplicate.
//
// Pure logic plus one fetch; the caller persists via store.setPreferences.

const HOUR = 3600 * 1000;

// Trips rarely change hour to hour, and every sync is a serverless invocation
// plus a third-party fetch, so throttle hard.
export const SYNC_INTERVAL_MS = 6 * HOUR;

/** Stable id for an imported trip, so re-syncs update rather than duplicate. */
export function feedTripId(trip) {
  const key = `${trip.start}_${trip.end}_${(trip.label || "").slice(0, 40)}`;
  return `tripfeed_${key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

export function isFeedTrip(trip) {
  return trip?.source === "feed";
}

/**
 * Merge freshly fetched trips into the existing list.
 *
 * Manual trips pass through untouched. Feed trips are replaced wholesale, so a
 * trip cancelled in TripIt disappears here too — but only among rows this sync
 * owns.
 */
export function mergeFeedTrips(existing, fetched) {
  const manual = (existing || []).filter(t => !isFeedTrip(t));
  const previous = new Map((existing || []).filter(isFeedTrip).map(t => [t.id, t]));

  const imported = (fetched || []).map(t => {
    const id = feedTripId(t);
    const prior = previous.get(id);
    return {
      // Preserve any notes the user added to an imported trip — the feed does
      // not own that field, so a re-sync must not wipe it.
      notes: prior?.notes || "",
      ...prior,
      id,
      label: t.label || prior?.label || "Trip",
      start: t.start,
      end: t.end,
      source: "feed",
      segments: t.segments || 1,
    };
  });

  return [...manual, ...imported];
}

/** Should we sync now? */
export function shouldSync(lastSyncedAt, now = Date.now()) {
  if (!lastSyncedAt) return true;
  const last = new Date(lastSyncedAt).getTime();
  if (Number.isNaN(last)) return true;
  return now - last >= SYNC_INTERVAL_MS;
}

/**
 * Fetch and normalize the feed. Throws with a readable message on failure so
 * the Settings card can show it; callers that sync in the background should
 * swallow it — a travel feed being down must never block the app.
 */
export async function fetchTripFeed(url) {
  if (!url) throw new Error("No feed URL set");
  const res = await fetch(`/api/trips-feed?url=${encodeURIComponent(url)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Feed request failed (${res.status})`);
  return data; // { trips, format, warning?, fetched_at }
}
